import { useCallback, useEffect, useState } from "react";
import { getTaskWorker, type TaskWorkerState } from "@/api/task-workers";
import { useCompanionStore } from "@/stores/companion-store";
import { useTasksStore } from "@/stores/tasks-store";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

export function useTaskWorker(
  projectId: string | undefined,
  taskId: string | undefined,
) {
  const [snapshot, setSnapshot] = useState<{
    key: string;
    worker: TaskWorkerState | null;
  }>();
  const [error, setError] = useState({ key: "", message: "" });
  const [revision, setRevision] = useState(0);
  // Without a task nothing is fetched: constant selections keep a host such
  // as a chat-target input bar from re-rendering on every Denker status.
  const refetchSignal = useTasksStore((state) =>
    taskId ? state.refetchSignal : 0,
  );
  const executionSignal = useCompanionStore((state) =>
    taskId
      ? Array.from(state.executions.values())
          .filter((execution) => execution.taskId === taskId)
          .map((execution) => `${execution.id}:${execution.status}`)
          .join(",")
      : "",
  );
  const key = `${projectId}:${taskId}`;
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (!projectId || !taskId) return;
    let active = true;
    void getTaskWorker(projectId, taskId)
      .then((worker) => {
        if (active) {
          setSnapshot({ key, worker });
          setError({ key, message: "" });
        }
      })
      .catch(() => {
        if (active)
          setError({
            key,
            message: translate(hooksMessages, "taskConversationLoadFailed"),
          });
      });
    return () => {
      active = false;
    };
  }, [projectId, taskId, key, refetchSignal, executionSignal, revision]);
  useEffect(() => {
    if (!projectId || !taskId || snapshot?.key !== key || !snapshot.worker)
      return;
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [projectId, taskId, key, snapshot?.key, snapshot?.worker, refresh]);
  const acceptState = useCallback(
    (worker: TaskWorkerState) => setSnapshot({ key, worker }),
    [key],
  );
  return {
    acceptState,
    worker: snapshot?.key === key ? snapshot.worker : null,
    // Keyed like the snapshot: a host that switches tasks without remounting
    // never shows the previous task's load error.
    error: error.key === key ? error.message : "",
    refresh,
  };
}
